import type { Location } from 'react-router-dom';
import type { AccountMailboxFolder } from '../useData';
import type { ComposeListRestoreState } from './composeNavigation';
import { loadEmailListReturnContext, type EmailListPaginationSnapshot } from './emailListReturnStorage';
import {
  loadEmailSidebarSelection,
  type EmailSidebarSelectionStored,
  type EmailSpecialView,
} from './emailSidebarSelectionStorage';

/** Mail list UI state resolved synchronously on mount (before the first list fetch). */
export type InitialEmailListState = {
  selectedAccount?: number;
  selectedImapFolderPath: string;
  selectedPersonalFolderId?: number;
  selectedShareId?: number;
  selectedSpecialView: EmailSpecialView | null;
  mentionsViewActive: boolean;
  selectedEmailId: number | null;
  selectedPartitionAt: string | null;
  restoreListPagination: EmailListPaginationSnapshot | null;
  /** True when navigation/session provided explicit restore hints. */
  hasExplicitRestore: boolean;
};

function applySidebarToInitial(
  target: InitialEmailListState,
  sidebar: EmailSidebarSelectionStored,
): void {
  switch (sidebar.kind) {
    case 'sharedAccount': {
      if (typeof sidebar.accountId === 'number') {
        target.selectedAccount = sidebar.accountId;
      }
      target.selectedImapFolderPath = sidebar.imapPath ?? sidebar.folder ?? 'INBOX';
      break;
    }
    case 'personalFolder':
      target.selectedPersonalFolderId = sidebar.id;
      break;
    case 'sharedFolder':
      target.selectedShareId = sidebar.shareId;
      break;
    case 'special':
      target.selectedSpecialView = sidebar.view;
      break;
    case 'mentions':
      target.mentionsViewActive = true;
      break;
    default:
      break;
  }
}

function resolveSidebarSelection(
  state: ComposeListRestoreState | null,
): EmailSidebarSelectionStored | null {
  if (state?.restoreSidebarSelection) return state.restoreSidebarSelection;
  if (state?.restoreSpecialView) return { kind: 'special', view: state.restoreSpecialView };
  if (state?.restoreMentionsView) return { kind: 'mentions' };
  const session = loadEmailListReturnContext();
  if (session?.sidebarSelection) return session.sidebarSelection;
  return loadEmailSidebarSelection();
}

/** Read restore hints from navigation state / session / localStorage once on mount. */
export function resolveInitialEmailListState(location: Location): InitialEmailListState {
  const state = (location.state as ComposeListRestoreState | null) ?? null;
  const session = loadEmailListReturnContext();

  const initial: InitialEmailListState = {
    selectedImapFolderPath: 'INBOX',
    selectedSpecialView: null,
    mentionsViewActive: false,
    selectedEmailId: state?.restoreSelectedEmailId ?? session?.restoreSelectedEmailId ?? null,
    selectedPartitionAt: state?.restorePartitionAt ?? session?.restorePartitionAt ?? null,
    restoreListPagination:
      state?.restoreListPagination ?? session?.restoreListPagination ?? null,
    hasExplicitRestore: Boolean(
      state?.restoreSidebarSelection ||
        state?.restoreSelectedEmailId != null ||
        state?.restoreSpecialView ||
        state?.restoreMentionsView ||
        state?.restoreListPagination ||
        session,
    ),
  };

  if (state?.restoreSpecialView) {
    initial.selectedSpecialView = state.restoreSpecialView;
    return initial;
  }
  if (session?.restoreSpecialView) {
    initial.selectedSpecialView = session.restoreSpecialView;
    return initial;
  }
  if (state?.restoreMentionsView || session?.mentionsViewActive) {
    initial.mentionsViewActive = true;
    return initial;
  }

  const sidebar = resolveSidebarSelection(state);
  if (sidebar && sidebar.kind !== 'all') {
    applySidebarToInitial(initial, sidebar);
  }

  return initial;
}

export function mailboxFolderFromImapPath(path: string): AccountMailboxFolder {
  if (path === 'MY_SENT') return 'MY_SENT';
  if (path === 'SENT') return 'SENT';
  if (path === 'DRAFT') return 'DRAFT';
  if (path === 'JUNK') return 'JUNK';
  return 'INBOX';
}
